import '../scss/components/_ownerreports.scss'
import React, { useState, useMemo } from 'react'

// ─── Constants ────────────────────────────────────────────────────────────────

const MONTHS_FULL = ['January','February','March','April','May','June','July','August','September','October','November','December']
const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

const fmtDate = str => {
  if (!str) return '—'
  return new Date(str).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

// ─── Shared StatTile ─────────────────────────────────────────────────────────

function StatTile({ label, value, sub, accent }) {
  return (
    <div className="rpt-stat-tile" style={{ borderTopColor: accent }}>
      <span className="rpt-stat-value" style={{ color: accent }}>{value}</span>
      <span className="rpt-stat-label">{label}</span>
      {sub && <span className="rpt-stat-sub">{sub}</span>}
    </div>
  )
}

// ─── Status badge ─────────────────────────────────────────────────────────────

const PV_CFG = {
  COMPLETED:   { label: 'Completed',   bg: '#F0FDF4', color: '#166534', dot: '#22C55E' },
  PENDING:     { label: 'Pending',     bg: '#FFFBEB', color: '#92400E', dot: '#F59E0B' },
  IN_PROGRESS: { label: 'In Progress', bg: '#EFF6FF', color: '#1D4ED8', dot: '#3B82F6' },
  REJECTED:    { label: 'Rejected',    bg: '#FFF1F2', color: '#991B1B', dot: '#EF4444' },
}

function PVBadge({ status }) {
  const cfg = PV_CFG[status] || PV_CFG.PENDING
  return (
    <span className="co-rpt-badge" style={{ background: cfg.bg, color: cfg.color }}>
      <span className="cp-rpt-dot" style={{ background: cfg.dot }} />{cfg.label}
    </span>
  )
}

function StatusBadge({ status }) {
  const cfg = status === 'returned'
    ? { bg: '#F0FDF4', color: '#166534', dot: '#22C55E', label: 'Returned' }
    : { bg: '#FFFBEB', color: '#92400E', dot: '#F59E0B', label: 'Outside'  }
  return (
    <span className="co-rpt-badge" style={{ background: cfg.bg, color: cfg.color }}>
      <span className="cp-rpt-dot" style={{ background: cfg.dot }} />{cfg.label}
    </span>
  )
}

// ─── Two-level accordion — matches OccupancyReport exactly ───────────────────
// Outer:  .rpt-occupancy-accordion  (white card with title)
// Year:   .rpt-year-accordion → .rpt-year-toggle → .rpt-year-content
// Month:  .rpt-month-accordion → .rpt-month-toggle → .rpt-month-content

function ComplianceAccordion({ title, years, renderMonthSummary, renderMonthTable, defaultOpenYear }) {
  const [expandedYears,  setExpandedYears]  = useState({ [defaultOpenYear]: true })
  const [expandedMonths, setExpandedMonths] = useState({})

  const toggleYear  = yr  => setExpandedYears(p  => ({ ...p, [yr]:  !p[yr]  }))
  const toggleMonth = key => setExpandedMonths(p => ({ ...p, [key]: !p[key] }))

  return (
    <div className="rpt-occupancy-accordion">
      <h3 className="rpt-accordion-title">{title}</h3>

      {years.map(({ year, totalCount, months }) => {
        const isYearOpen = !!expandedYears[year]
        return (
          <div key={year} className="rpt-year-accordion">
            <button className="rpt-year-toggle" onClick={() => toggleYear(year)}>
              <span className="rpt-accordion-chevron">{isYearOpen ? '▼' : '▶'}</span>
              <span className="rpt-year-label">{year}</span>
              <span className="rpt-year-count">({totalCount} records)</span>
            </button>

            {isYearOpen && (
              <div className="rpt-year-content">
                {months.map(({ monthIdx, monthName, records }) => {
                  const key        = `${year}-${monthIdx}`
                  const isMonthOpen = !!expandedMonths[key]
                  return (
                    <div key={monthIdx} className="rpt-month-accordion">
                      <button className="rpt-month-toggle" onClick={() => toggleMonth(key)}>
                        <span className="rpt-accordion-chevron">{isMonthOpen ? '▼' : '▶'}</span>
                        <span className="rpt-month-label">{monthName}</span>
                        <span className="rpt-month-count">({records.length} records)</span>
                      </button>

                      {isMonthOpen && (
                        <div className="rpt-month-content">
                          {renderMonthSummary && renderMonthSummary(records)}
                          {records.length > 0
                            ? renderMonthTable(records)
                            : <p className="rpt-empty-message">No records this month.</p>
                          }
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ─── Build year→month structure from flat array ───────────────────────────────
// Returns [{ year, totalCount, months: [{ monthIdx, monthName, records }] }]
// sorted years descending, months ascending within each year

function groupByYearMonth(records, yearKey = '_year', monthKey = '_month') {
  const map = {}
  records.forEach(r => {
    const yr = r[yearKey]
    const mi = r[monthKey]
    if (!map[yr]) map[yr] = {}
    if (!map[yr][mi]) map[yr][mi] = []
    map[yr][mi].push(r)
  })

  return Object.keys(map)
    .map(Number)
    .sort((a, b) => b - a)
    .map(year => {
      const monthsMap = map[year]
      const months = Object.keys(monthsMap)
        .map(Number)
        .sort((a, b) => a - b)
        .map(mi => ({
          monthIdx:  mi,
          monthName: MONTHS_FULL[mi],
          records:   monthsMap[mi],
        }))
      const totalCount = months.reduce((s, m) => s + m.records.length, 0)
      return { year, totalCount, months }
    })
}

// ═══════════════════════════════════════════════════════════════════════════════
// DUMMY DATA
// ═══════════════════════════════════════════════════════════════════════════════

function seedRand(seed) {
  let s = seed
  return () => { s = (s * 9301 + 49297) % 233280; return s / 233280 }
}

const ROOM_REGISTRY = [
  { room: '101', bedCount: 1 },
  { room: '102', bedCount: 2 },
  { room: '103', bedCount: 2 },
  { room: '201', bedCount: 3 },
  { room: '202', bedCount: 2 },
  { room: '203', bedCount: 1 },
  { room: '301', bedCount: 3 },
  { room: '302', bedCount: 2 },
  { room: '303', bedCount: 3 },
]
const ROOMS = ROOM_REGISTRY.map(r => r.room)

function randomBed(rand, roomNumber) {
  const entry = ROOM_REGISTRY.find(r => r.room === roomNumber)
  const count = entry ? entry.bedCount : 2
  return String(1 + Math.floor(rand() * count)).padStart(2, '0')
}

const TENANTS  = ['Rahul Sharma','Sneha Reddy','Arjun Choudhary','Pooja Gupta','Meena Dasari','Vikas Rao','Kiran Menon','Deepak Joshi','Ananya Singh','Suresh Iyer','Priya Nair','Naveen Pillai','Sonia Agarwal','Ravi Kumar','Amit Verma']
const VISITORS = ['Suresh Sharma','Lakshmi Reddy','Pradeep C.','Anita Gupta','Mohan D.','Geeta Rao','Rajan M.','Kavya J.','Vikram S.','Smitha I.']
const RELATIONS = ['Parent','Sibling','Friend','Cousin','Spouse','Uncle/Aunt']
const CHECKOUT_REASONS = ['Family visit','Medical appointment','Festival at home','Work travel','Personal work','Vacation']
const PV_STATUSES = ['COMPLETED','COMPLETED','COMPLETED','PENDING','IN_PROGRESS','REJECTED']

const DUMMY_CHECKOUT = (() => {
  const rand = seedRand(11)
  const out = []
  let id = 1
  ;[2024, 2025, 2026].forEach(yr => {
    const maxMonth = yr === 2026 ? 5 : 11
    for (let mi = 0; mi <= maxMonth; mi++) {
      const n = 2 + Math.floor(rand() * 5)
      for (let j = 0; j < n; j++) {
        const roomIdx  = Math.floor(rand() * ROOMS.length)
        const room     = ROOMS[roomIdx]
        const day      = 1 + Math.floor(rand() * 25)
        const duration = 1 + Math.floor(rand() * 6)
        const outDate  = new Date(yr, mi, day)
        const inDate   = new Date(yr, mi, day + duration)
        out.push({
          id: id++,
          tenant:   TENANTS[Math.floor(rand() * TENANTS.length)],
          room,
          bed:      randomBed(rand, room),
          reason:   CHECKOUT_REASONS[Math.floor(rand() * CHECKOUT_REASONS.length)],
          outDate:  outDate.toISOString(),
          inDate:   duration < 6 ? inDate.toISOString() : null,
          duration,
          status:   duration < 6 ? 'returned' : 'out',
          _year:    String(yr),
          _month:   mi,
        })
      }
    }
  })
  
  // ─── Override specific records for June 2026 to show outside cases ───
  // Find records in June 2026 and modify the first 3 to be "outside"
  const june2026Records = out.filter(r => r._year === '2026' && r._month === 5)
  
  if (june2026Records.length >= 3) {
    // Override first 3 June records
    const recordsToModify = june2026Records.slice(0, 3)
    
    // Record 1: Went out June 25, expected back July 4
    recordsToModify[0].status = 'out'
    recordsToModify[0].outDate = new Date(2026, 5, 25).toISOString()
    recordsToModify[0].inDate = new Date(2026, 6, 4).toISOString()
    recordsToModify[0].duration = 9
    recordsToModify[0].reason = 'Family visit'
    
    // Record 2: Went out June 27, expected back July 3
    recordsToModify[1].status = 'out'
    recordsToModify[1].outDate = new Date(2026, 5, 27).toISOString()
    recordsToModify[1].inDate = new Date(2026, 6, 3).toISOString()
    recordsToModify[1].duration = 6
    recordsToModify[1].reason = 'Medical appointment'
    
    // Record 3: Went out June 28, expected back July 5
    recordsToModify[2].status = 'out'
    recordsToModify[2].outDate = new Date(2026, 5, 28).toISOString()
    recordsToModify[2].inDate = new Date(2026, 6, 5).toISOString()
    recordsToModify[2].duration = 7
    recordsToModify[2].reason = 'Vacation'
  }
  
  return out
})()

const DUMMY_POLICE = (() => {
  const rand = seedRand(22)
  const out = []
  let id = 1
  ;[2024, 2025, 2026].forEach(yr => {
    const maxMonth = yr === 2026 ? 5 : 11
    for (let mi = 0; mi <= maxMonth; mi++) {
      const n = 1 + Math.floor(rand() * 3)
      for (let j = 0; j < n; j++) {
        const roomIdx        = Math.floor(rand() * ROOMS.length)
        const room           = ROOMS[roomIdx]
        const day            = 1 + Math.floor(rand() * 25)
        const status         = PV_STATUSES[Math.floor(rand() * PV_STATUSES.length)]
        const submitted      = new Date(yr, mi, day)
        const daysToComplete = status === 'COMPLETED' ? 7 + Math.floor(rand() * 21) : null
        const completed      = daysToComplete ? new Date(yr, mi, day + daysToComplete) : null
        out.push({
          id: id++,
          tenant:          TENANTS[Math.floor(rand() * TENANTS.length)],
          room,
          bed:             randomBed(rand, room),
          status,
          submitted:       submitted.toISOString(),
          completed:       completed ? completed.toISOString() : null,
          daysToComplete,
          hasDoc:          status === 'COMPLETED' && rand() > 0.2,
          _year:           String(yr),
          _month:          mi,
        })
      }
    }
  })
  return out
})()

const DUMMY_VISITORS = (() => {
  const rand = seedRand(33)
  const out = []
  let id = 1
  ;[2024, 2025, 2026].forEach(yr => {
    const maxMonth = yr === 2026 ? 5 : 11
    const SEASONAL = [1.3,0.9,0.9,1.2,0.8,0.8,0.8,0.9,0.9,1.3,1.4,1.0]
    for (let mi = 0; mi <= maxMonth; mi++) {
      const n = Math.round(5 * SEASONAL[mi]) + Math.floor(rand() * 3)
      for (let j = 0; j < n; j++) {
        const roomIdx = Math.floor(rand() * ROOMS.length)
        const room    = ROOMS[roomIdx]
        out.push({
          id: id++,
          tenant:      TENANTS[Math.floor(rand() * TENANTS.length)],
          room,
          bed:         randomBed(rand, room),
          visitorName: VISITORS[Math.floor(rand() * VISITORS.length)],
          relation:    RELATIONS[Math.floor(rand() * RELATIONS.length)],
          inDate:      new Date(yr, mi, 1 + Math.floor(rand() * 27)).toISOString(),
          stayDays:    1 + Math.floor(rand() * 3),
          _year:       String(yr),
          _month:      mi,
        })
      }
    }
  })
  return out
})()

// ═══════════════════════════════════════════════════════════════════════════════
// CHECK-IN / CHECK-OUT TAB
// ═══════════════════════════════════════════════════════════════════════════════

function CheckInOutTab({ selectedYear }) {
  const yearData    = DUMMY_CHECKOUT.filter(r => r._year === selectedYear)
  const returned    = yearData.filter(r => r.status === 'returned').length
  const stillOut    = yearData.filter(r => r.status === 'out').length
  const avgDuration = yearData.length
    ? (yearData.reduce((s, r) => s + r.duration, 0) / yearData.length).toFixed(1)
    : 0
  const tenantCounts = {}
  yearData.forEach(r => { tenantCounts[r.tenant] = (tenantCounts[r.tenant] || 0) + 1 })
  const topTenant = Object.entries(tenantCounts).sort((a,b) => b[1]-a[1])[0]

  const years = useMemo(() => groupByYearMonth(DUMMY_CHECKOUT), [])

  const renderMonthTable = records => (
    <table className="rpt-occupancy-table">
      <thead>
        <tr>
          <th>S.No</th>
          <th>Tenant</th>
          <th>Room</th>
          <th>Bed</th>
          <th>Reason</th>
          <th>Went out</th>
          <th>Returning date</th>
          <th>Days out</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {records.map((r, i) => (
          <tr key={r.id}>
            <td>{i + 1}</td>
            <td>{r.tenant}</td>
            <td>{r.room}</td>
            <td>{r.bed}</td>
            <td>{r.reason}</td>
            <td>{fmtDate(r.outDate)}</td>
            <td>{r.inDate ? fmtDate(r.inDate) : '—'}</td>
            <td>{r.duration}d</td>
            <td><StatusBadge status={r.status} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  )

  return (
    <div className="rpt-section">
      <div className="rpt-tiles">
        <StatTile label="Total exits this year"  value={yearData.length}      sub={`${selectedYear} year-to-date`}                          accent="#6366F1" />
        <StatTile label="Returned safely"         value={returned}             sub="Back in PG"                                              accent="#22C55E" />
        <StatTile label="Currently outside"       value={stillOut}             sub="Not yet returned"                                        accent="#F59E0B" />
        <StatTile label="Avg days out"            value={`${avgDuration}d`}   sub={topTenant ? `Most frequent: ${topTenant[0].split(' ')[0]}` : '—'} accent="#EF4444" />
      </div>
      <div className="cp-rpt-card">
        <div className="cp-rpt-card-title">
          Monthly check-in/out activity <span className="cp-rpt-card-sub">{selectedYear}</span>
        </div>
        <div className="cp-rpt-sparkbars">
          {MONTHS_SHORT.map((_, mi) => {
            const monthRecords = yearData.filter(r => r._month === mi)
            const count = monthRecords.length
            const returnedCount = monthRecords.filter(r => r.status === 'returned').length
            const outsideCount = monthRecords.filter(r => r.status === 'out').length
            const maxV = Math.max(...MONTHS_SHORT.map((_, i) => yearData.filter(r => r._month === i).length), 1)
            const h = Math.max(4, Math.round((count / maxV) * 100))
            return (
              <div key={mi} className="cp-rpt-spark-col">
                <span className="cp-rpt-spark-val">{count}</span>
                <div className="cp-rpt-spark-bar" style={{ height: `${h}%`, background: count > 0 ? '#6366F1' : '#E5E7EB' }} />
                <span className="cp-rpt-spark-lbl">{MONTHS_SHORT[mi]}</span>
              </div>
            )
          })}
        </div>
      </div>
      <ComplianceAccordion
        title="Month Wise Check-in / Check-out"
        years={years}
        renderMonthTable={renderMonthTable}
        defaultOpenYear={Number(selectedYear)}
      />
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// POLICE VERIFICATION TAB
// ═══════════════════════════════════════════════════════════════════════════════

function PoliceVerificationTab({ selectedYear }) {
  const yearData   = DUMMY_POLICE.filter(r => r._year === selectedYear)
  const completed  = yearData.filter(r => r.status === 'COMPLETED')
  const pending    = yearData.filter(r => r.status === 'PENDING')
  const inProgress = yearData.filter(r => r.status === 'IN_PROGRESS')
  const rejected   = yearData.filter(r => r.status === 'REJECTED')
  const withDoc    = yearData.filter(r => r.hasDoc)
  const avgDays    = completed.filter(r => r.daysToComplete).length
    ? Math.round(completed.reduce((s, r) => s + (r.daysToComplete || 0), 0) / completed.filter(r => r.daysToComplete).length)
    : 0

  const years = useMemo(() => groupByYearMonth(DUMMY_POLICE), [])

  const renderMonthSummary = records => {
    const c = records.filter(r => r.status === 'COMPLETED').length
    const p = records.filter(r => r.status === 'PENDING').length
    const ip = records.filter(r => r.status === 'IN_PROGRESS').length
    const rj = records.filter(r => r.status === 'REJECTED').length
    return (
      <div className="co-rpt-month-stats">
        {c  > 0 && <span style={{ color: '#166534' }}>✓ {c} completed</span>}
        {p  > 0 && <span style={{ color: '#92400E' }}>⏳ {p} pending</span>}
        {ip > 0 && <span style={{ color: '#1D4ED8' }}>⟳ {ip} in progress</span>}
        {rj > 0 && <span style={{ color: '#991B1B' }}>✗ {rj} rejected</span>}
      </div>
    )
  }

  const renderMonthTable = records => (
    <table className="rpt-occupancy-table">
      <thead>
        <tr>
          <th>S.No</th>
          <th>Tenant</th>
          <th>Room</th>
          <th>Bed</th>
          <th>Submitted</th>
          <th>Completed</th>
          <th>Days taken</th>
          <th>Status</th>
          <th>Ack. doc</th>
        </tr>
      </thead>
      <tbody>
        {records.map((r, i) => (
          <tr key={r.id}>
            <td>{i + 1}</td>
            <td>{r.tenant}</td>
            <td>{r.room}</td>
            <td>{r.bed}</td>
            <td>{fmtDate(r.submitted)}</td>
            <td>{r.completed ? fmtDate(r.completed) : '—'}</td>
            <td>
              {r.daysToComplete
                ? <span style={{ color: r.daysToComplete > 21 ? '#991B1B' : '#166534', fontWeight: 600 }}>{r.daysToComplete}d</span>
                : '—'}
            </td>
            <td><PVBadge status={r.status} /></td>
            <td>
              {r.hasDoc
                ? <button className="co-rpt-doc-btn">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3"/></svg>
                    Download
                  </button>
                : <span style={{ color: '#9CA3AF', fontSize: 12 }}>—</span>
              }
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )

  return (
    <div className="rpt-section">
      <div className="rpt-tiles">
        <StatTile label="Submitted"              value={yearData.length}                sub={selectedYear}                        accent="#6366F1" />
        <StatTile label="Completed"              value={completed.length}               sub={`${withDoc.length} docs available`}  accent="#22C55E" />
        <StatTile label="Pending / In-progress"  value={pending.length + inProgress.length} sub="Awaiting police response"       accent="#F59E0B" />
        <StatTile label="Avg completion"         value={avgDays ? `${avgDays}d` : '—'} sub="Days taken to complete"              accent="#6366F1" />
      </div>
      <div className="cp-rpt-card">
        <div className="cp-rpt-card-title">
          Monthly police verification requests <span className="cp-rpt-card-sub">{selectedYear}</span>
        </div>
        <div className="cp-rpt-sparkbars">
          {MONTHS_SHORT.map((_, mi) => {
            const monthRecords = yearData.filter(r => r._month === mi)
            const count = monthRecords.length
            const completed = monthRecords.filter(r => r.status === 'COMPLETED').length
            const pending = monthRecords.filter(r => r.status === 'PENDING').length
            const inProgress = monthRecords.filter(r => r.status === 'IN_PROGRESS').length
            const rejected = monthRecords.filter(r => r.status === 'REJECTED').length
            const maxV = Math.max(...MONTHS_SHORT.map((_, i) => yearData.filter(r => r._month === i).length), 1)
            const h = Math.max(4, Math.round((count / maxV) * 100))
            return (
              <div key={mi} className="cp-rpt-spark-col">
                <span className="cp-rpt-spark-val">{count}</span>
                <div className="cp-rpt-spark-bar" style={{ height: `${h}%`, background: count > 0 ? '#6366F1' : '#E5E7EB' }} />
                <span className="cp-rpt-spark-lbl">{MONTHS_SHORT[mi]}</span>
              </div>
            )
          })}
        </div>
      </div>
      {/* {(pending.length + inProgress.length) > 0 && (
        <div className="cp-rpt-verdict-card cp-rpt-verdict-ok">
          <p className="cp-rpt-verdict-text">
            {pending.length + inProgress.length} police verification{pending.length + inProgress.length !== 1 ? 's are' : ' is'} still open.
            {rejected.length > 0 ? ` ${rejected.length} were rejected and may need resubmission.` : ' No rejections this year.'}
          </p>
          <div className="cp-rpt-verdict-pills">
            <span className="cp-rpt-vpill cp-rpt-vpill-green"><span>✓</span><span className="vpl">Completed</span><strong>{completed.length}</strong></span>
            <span className="cp-rpt-vpill cp-rpt-vpill-blue"><span>⏳</span><span className="vpl">Pending</span><strong>{pending.length}</strong></span>
            <span className="cp-rpt-vpill cp-rpt-vpill-gray"><span>🔄</span><span className="vpl">In progress</span><strong>{inProgress.length}</strong></span>
            {rejected.length > 0 && <span className="cp-rpt-vpill cp-rpt-vpill-red"><span>✗</span><span className="vpl">Rejected</span><strong>{rejected.length}</strong></span>}
          </div>
        </div>
      )} */}

      <ComplianceAccordion
        title="Month Wise Police Verification"
        years={years}
        renderMonthSummary={renderMonthSummary}
        renderMonthTable={renderMonthTable}
        defaultOpenYear={Number(selectedYear)}
      />
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// VISITOR LOG TAB
// ═══════════════════════════════════════════════════════════════════════════════

function VisitorLogTab({ selectedYear }) {
  const yearData       = DUMMY_VISITORS.filter(r => r._year === selectedYear)
  const totalVisitors  = yearData.length
  const uniqueTenants  = new Set(yearData.map(r => r.tenant)).size
  const avgStay        = yearData.length
    ? (yearData.reduce((s, r) => s + r.stayDays, 0) / yearData.length).toFixed(1)
    : 0
  const byMonthCount   = MONTHS_SHORT.map((_, mi) => yearData.filter(r => r._month === mi).length)
  const peakMi         = byMonthCount.indexOf(Math.max(...byMonthCount))
  const roomCounts     = {}
  yearData.forEach(r => { roomCounts[r.room] = (roomCounts[r.room] || 0) + 1 })
  const topRoom        = Object.entries(roomCounts).sort((a,b) => b[1]-a[1])[0]

  const years = useMemo(() => groupByYearMonth(DUMMY_VISITORS), [])

  const renderMonthTable = records => (
    <table className="rpt-occupancy-table">
      <thead>
        <tr>
          <th>S.No</th>
          <th>Tenant</th>
          <th>Room</th>
          <th>Bed</th>
          <th>Visitor name</th>
          <th>Relation</th>
          <th>Visit date</th>
          <th>Days stayed</th>
        </tr>
      </thead>
      <tbody>
        {records.map((r, i) => (
          <tr key={r.id}>
            <td>{i + 1}</td>
            <td>{r.tenant}</td>
            <td>{r.room}</td>
            <td>{r.bed}</td>
            <td>{r.visitorName}</td>
            <td>{r.relation}</td>
            <td>{fmtDate(r.inDate)}</td>
            <td>{r.stayDays}d</td>
          </tr>
        ))}
      </tbody>
    </table>
  )

  return (
    <div className="rpt-section">
      <div className="rpt-tiles">
        <StatTile label="Total visitors"          value={totalVisitors}          sub={`${selectedYear} year-to-date`}                   accent="#6366F1" />
        <StatTile label="Tenants with visitors"   value={uniqueTenants}          sub="had guests this year"                             accent="#22C55E" />
        <StatTile label="Peak visitor month"      value={MONTHS_SHORT[peakMi]}   sub={`${byMonthCount[peakMi]} visitors`}               accent="#F59E0B" />
        <StatTile label="Avg stay"                value={`${avgStay}d`}          sub={topRoom ? `Most visited: Room ${topRoom[0]}` : '—'} accent="#6366F1" />
      </div>

      {/* Monthly sparkbar — kept as it adds visual value the accordion doesn't give */}
      <div className="cp-rpt-card">
        <div className="cp-rpt-card-title">
          Monthly visitor volume <span className="cp-rpt-card-sub">{selectedYear}</span>
        </div>
        <div className="cp-rpt-sparkbars">
          {byMonthCount.map((n, i) => {
            const maxV   = Math.max(...byMonthCount, 1)
            const h      = Math.max(4, Math.round((n / maxV) * 100))
            const isPeak = i === peakMi
            return (
              <div key={i} className="cp-rpt-spark-col">
                <span className="cp-rpt-spark-val">{n}</span>
                <div className="cp-rpt-spark-bar" style={{ height: `${h}%`, background: isPeak ? '#6366F1' : '#F06E18' }} />
                <span className="cp-rpt-spark-lbl">{MONTHS_SHORT[i]}</span>
              </div>
            )
          })}
        </div>
        <p className="cp-rpt-card-hint">Purple = peak month · Orange = regular months</p>
      </div>

      <ComplianceAccordion
        title="Month Wise Visitor Log"
        years={years}
        renderMonthTable={renderMonthTable}
        defaultOpenYear={Number(selectedYear)}
      />
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════════════
// ROOT — OwnerComplianceReport
// ═══════════════════════════════════════════════════════════════════════════════

const OwnerComplianceReport = ({ selectedPGId, selectedPG, selectedYear }) => {
  const [tab, setTab] = useState('checkinout')

  const AVAILABLE_YEARS = ['2024','2025','2026']
  const yearStr = AVAILABLE_YEARS.includes(String(selectedYear))
    ? String(selectedYear)
    : AVAILABLE_YEARS[AVAILABLE_YEARS.length - 1]

  const checkouts = DUMMY_CHECKOUT.filter(r => r._year === yearStr)
  const police    = DUMMY_POLICE.filter(r => r._year === yearStr)
  const visitors  = DUMMY_VISITORS.filter(r => r._year === yearStr)

  return (
    <div className="cp-rpt-root">
      {/* <div className="rpt-tiles" style={{ marginBottom: 16 }}>
        <StatTile label="Check-in/out exits"      value={checkouts.length} sub={`${checkouts.filter(r=>r.status==='out').length} still outside`}      accent="#6366F1" />
        <StatTile label="Police verifications"    value={police.length}    sub={`${police.filter(r=>r.status==='COMPLETED').length} completed`}         accent="#22C55E" />
        <StatTile label="Visitor entries"         value={visitors.length}  sub={`${new Set(visitors.map(r=>r.tenant)).size} tenants had guests`}        accent="#F59E0B" />
        <StatTile label="PV pending/in-progress"  value={police.filter(r=>['PENDING','IN_PROGRESS'].includes(r.status)).length} sub="Awaiting response" accent="#EF4444" />
      </div> */}

      <div className="cp-rpt-view-tabs">
        {[
          { key: 'checkinout', label: 'Check-in / Out'       },
          { key: 'police',     label: 'Police Verification'   },
          { key: 'visitors',   label: 'Visitor Log'           },
        ].map(t => (
          <button
            key={t.key}
            className={`cp-rpt-vtab${tab === t.key ? ' on' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'checkinout' && <CheckInOutTab  selectedYear={yearStr} />}
      {tab === 'police'     && <PoliceVerificationTab selectedYear={yearStr} />}
      {tab === 'visitors'   && <VisitorLogTab  selectedYear={yearStr} />}
    </div>
  )
}

export default OwnerComplianceReport